// frontend/src/utils/bookingScheduleUtils.js

import moment from 'moment-timezone';
import { formatDateISO, parseLocalDate } from './dateTimeFormatter';

export const normalizeBookingOccurrenceDate = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : formatDateISO(value);
  }

  if (typeof value !== 'string') {
    return null;
  }

  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})(?:T|$)/);
  return match ? match[1] : null;
};

export const deriveBookingOccurrenceScope = ({
  bookingDate,
  selectedDates = [],
  availableDates = [],
  bookAllAvailableDates = false,
  canBookRecurring = false,
} = {}) => {
  const sourceDates = bookAllAvailableDates
    ? availableDates
    : (canBookRecurring ? selectedDates : [bookingDate]);
  const occurrenceDates = Array.from(new Set(
    sourceDates.map(normalizeBookingOccurrenceDate).filter(Boolean),
  ));
  return {
    occurrenceDates,
    selectedOccurrenceDate: !bookAllAvailableDates && occurrenceDates.length === 1
      ? occurrenceDates[0]
      : null,
  };
};

const normalizePositiveId = (value) => {
  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
};

const normalizeScheduleTime = (value) => {
  const match = String(value || '').match(/^(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : null;
};

/**
 * Prove that the selected trip belongs to the completed availability snapshot
 * for the one date currently represented by the modal. A matching wall time
 * alone is intentionally insufficient because an older date can retain the
 * same HH:mm while a replacement availability request is still pending.
 */
export const resolveExactDelayIdentity = ({
  slotAvailability = [],
  slotAvailabilityDate,
  slotAvailabilityLoading = false,
  selectedOccurrenceDate,
  booking,
} = {}) => {
  const selectedDate = normalizeBookingOccurrenceDate(selectedOccurrenceDate);
  const loadedDate = normalizeBookingOccurrenceDate(slotAvailabilityDate);
  const selectedTime = normalizeScheduleTime(
    typeof booking?.schedule === 'string'
      ? booking.schedule
      : booking?.schedule?.time,
  );
  const selectedTripId = normalizePositiveId(booking?.selectedTripId);

  if (
    slotAvailabilityLoading
    || !selectedDate
    || selectedDate !== loadedDate
    || !selectedTime
    || !selectedTripId
  ) {
    return { exact: false, tripId: null, minutes: 0 };
  }

  const candidate = slotAvailability.find((entry) => (
    normalizeScheduleTime(entry?.time) === selectedTime
    && normalizePositiveId(entry?.tripId ?? entry?.trip_id) === selectedTripId
  ));
  if (!candidate) {
    return { exact: false, tripId: null, minutes: 0 };
  }

  const selectedScheduleId = normalizePositiveId(booking?.selectedScheduleId);
  if (selectedScheduleId) {
    const candidateScheduleId = normalizePositiveId(
      candidate?.scheduleId ?? candidate?.occurrenceScheduleId,
    );
    if (candidateScheduleId !== selectedScheduleId) {
      return { exact: false, tripId: null, minutes: 0 };
    }
  }

  const delayMinutes = Number(candidate?.operationalDelay?.originDepartureMinutes);
  return {
    exact: true,
    tripId: selectedTripId,
    minutes: Number.isInteger(delayMinutes) && delayMinutes > 0 ? delayMinutes : 0,
  };
};

/**
 * Calculate every available date within the provided range for an active schedule.
 */
export const getScheduleAvailableDates = (schedule, startDate, endDate) => {
  const dates = new Set();
  if (!schedule?.active) return dates;

  const scheduleStart = parseLocalDate(schedule.start_date);
  const scheduleEnd = parseLocalDate(schedule.end_date);
  if (!scheduleStart || !scheduleEnd || Number.isNaN(scheduleStart.getTime()) || Number.isNaN(scheduleEnd.getTime())) {
    return dates;
  }
  const rangeStart = new Date(Math.max(startDate.getTime(), scheduleStart.getTime()));
  const rangeEnd = new Date(Math.min(endDate.getTime(), scheduleEnd.getTime()));

  if (rangeStart > rangeEnd) return dates;

  for (let d = new Date(rangeStart); d <= rangeEnd; d.setDate(d.getDate() + 1)) {
    const currentDate = new Date(d);
    if (isDayIncluded(currentDate, schedule.included_days) && !isDateExcluded(currentDate, schedule.excluded_periods)) {
      dates.add(formatDateISO(currentDate));
    }
  }

  return dates;
};

export const isDayIncluded = (date, includedDays) => {
  if (!includedDays) return false;
  const dateObj = parseLocalDate(date);
  if (!dateObj || Number.isNaN(dateObj.getTime())) return false;
  const day = dateObj.getDay();
  const includedDaysArray = includedDays.split(',').map((d) => parseInt(d.trim(), 10));
  return includedDaysArray.includes(day);
};

export const isDateExcluded = (date, excludedPeriods) => {
  if (!excludedPeriods?.length) return false;
  const checkDateObj = parseLocalDate(date);
  if (!checkDateObj || Number.isNaN(checkDateObj.getTime())) return false;
  const checkDate = new Date(checkDateObj).setHours(0, 0, 0, 0);

  return excludedPeriods.some((period) => {
    const startDateObj = parseLocalDate(period.start_date);
    const endDateObj = parseLocalDate(period.end_date);
    if (!startDateObj || !endDateObj) return false;
    const startDate = new Date(startDateObj).setHours(0, 0, 0, 0);
    const endDate = new Date(endDateObj).setHours(0, 0, 0, 0);
    return checkDate >= startDate && checkDate <= endDate;
  });
};

export const isDateInRange = (date, startDate, endDate) => {
  const checkDateObj = parseLocalDate(date);
  const startDateObj = parseLocalDate(startDate);
  const endDateObj = parseLocalDate(endDate);
  if (!checkDateObj || !startDateObj || !endDateObj) return false;
  const checkDate = new Date(checkDateObj).setHours(0, 0, 0, 0);
  const start = new Date(startDateObj).setHours(0, 0, 0, 0);
  const end = new Date(endDateObj).setHours(0, 0, 0, 0);
  return checkDate >= start && checkDate <= end;
};

/**
 * Produce fixed and on-call time slots while respecting overlaps and priorities.
 */
export const generateTimeSlotsFromSchedule = (schedules, selectedDate) => {
  if (!selectedDate) return [];

  const dateString = formatDateISO(new Date(selectedDate));
  const sortedSchedules = [...schedules].sort((a, b) => a.priority - b.priority);
  const occupiedRanges = [];
  const results = [];

  sortedSchedules.forEach((schedule) => {
    if (schedule.type === 'fixed' && schedule.time_slots?.length) {
      schedule.time_slots.forEach((slot) => {
        const slotStart = new Date(`${dateString}T${slot.start_time}`);
        const slotEnd = new Date(`${dateString}T${slot.end_time}`);
        const overlaps = occupiedRanges
          .filter((range) => !(slotEnd <= range.start || slotStart >= range.end))
          .sort((a, b) => a.start - b.start);

        if (!overlaps.length) {
          occupiedRanges.push({ start: slotStart, end: slotEnd });
          let currentTime = new Date(slotStart);
          while (currentTime <= slotEnd) {
            results.push({
              type: 'fixed',
              time: currentTime.toTimeString().substring(0, 5),
              scheduleId: schedule.id,
              priority: schedule.priority,
            });
            currentTime.setMinutes(currentTime.getMinutes() + (slot.frequency || 60));
          }
        } else {
          let currentStart = slotStart;
          overlaps.forEach((overlap) => {
            if (currentStart < overlap.start) {
              let currentTime = new Date(currentStart);
              while (currentTime < overlap.start) {
                results.push({
                  type: 'fixed',
                  time: currentTime.toTimeString().substring(0, 5),
                  scheduleId: schedule.id,
                  priority: schedule.priority,
                });
                currentTime.setMinutes(currentTime.getMinutes() + (slot.frequency || 60));
              }
              occupiedRanges.push({ start: currentStart, end: overlap.start });
            }
            currentStart = overlap.end;
          });

          if (currentStart < slotEnd) {
            let currentTime = new Date(currentStart);
            while (currentTime <= slotEnd) {
              results.push({
                type: 'fixed',
                time: currentTime.toTimeString().substring(0, 5),
                scheduleId: schedule.id,
                priority: schedule.priority,
              });
              currentTime.setMinutes(currentTime.getMinutes() + (slot.frequency || 60));
            }
            occupiedRanges.push({ start: currentStart, end: slotEnd });
          }
        }
      });
    } else if (schedule.type === 'on_call') {
      const callStart = new Date(`${dateString}T${schedule.on_call_start_time}`);
      const callEnd = new Date(`${dateString}T${schedule.on_call_end_time}`);
      const overlaps = occupiedRanges
        .filter((range) => !(callEnd <= range.start || callStart >= range.end))
        .sort((a, b) => a.start - b.start);

      if (!overlaps.length) {
        occupiedRanges.push({ start: callStart, end: callEnd });
        results.push({
          type: 'on_call',
          startTime: schedule.on_call_start_time.substring(0, 5),
          endTime: schedule.on_call_end_time.substring(0, 5),
          minGap: schedule.on_call_min_gap,
          scheduleId: schedule.id,
          priority: schedule.priority,
        });
      } else {
        let currentStart = callStart;
        overlaps.forEach((overlap) => {
          if (currentStart < overlap.start) {
            results.push({
              type: 'on_call',
              startTime: currentStart.toTimeString().substring(0, 5),
              endTime: overlap.start.toTimeString().substring(0, 5),
              minGap: schedule.on_call_min_gap,
              scheduleId: schedule.id,
              priority: schedule.priority,
            });
            occupiedRanges.push({ start: currentStart, end: overlap.start });
          }
          currentStart = overlap.end;
        });

        if (currentStart < callEnd) {
          results.push({
            type: 'on_call',
            startTime: currentStart.toTimeString().substring(0, 5),
            endTime: schedule.on_call_end_time.substring(0, 5),
            minGap: schedule.on_call_min_gap,
            scheduleId: schedule.id,
            priority: schedule.priority,
          });
          occupiedRanges.push({ start: currentStart, end: callEnd });
        }
      }
    }
  });

  return results;
};

export const sortTimeSlots = (slots) =>
  [...slots].sort((a, b) => {
    // Sort by time first (chronological order for display)
    const timeA = a.time || a.startTime;
    const timeB = b.time || b.startTime;
    const timeComparison = timeA.localeCompare(timeB);
    if (timeComparison !== 0) return timeComparison;

    // If same time, sort by type (fixed before on_call)
    if (a.type !== b.type) return a.type === 'fixed' ? -1 : 1;

    // If same time and type, sort by priority
    return a.priority - b.priority;
  });

/**
 * Pick the highest priority schedule that applies to the provided date/time.
 */
export const findApplicableSchedule = (date, time, schedules) => {
  if (!date || !schedules?.length) return null;

  const sortedSchedules = [...schedules].sort((a, b) => a.priority - b.priority);
  const applicableSchedules = sortedSchedules.filter(
    (schedule) =>
      schedule.active &&
      isDateInRange(date, schedule.start_date, schedule.end_date) &&
      isDayIncluded(date, schedule.included_days) &&
      !isDateExcluded(date, schedule.excluded_periods),
  );

  if (!applicableSchedules.length) {
    return null;
  }

  const rawTime = typeof time === 'string' ? time : time?.time;
  const timeString = rawTime ? rawTime.substring(0, 5) : null;

  const match = applicableSchedules.find((schedule) => {
    if (schedule.type === 'fixed') {
      return schedule.time_slots?.some((slot) => {
        if (!timeString) return false;
        if (slot.type === 'single') {
          return slot.start_time.substring(0, 5) === timeString;
        }
        if (slot.type === 'recurring') {
          const startTime = new Date(`2000-01-01T${slot.start_time}`);
          const endTime = new Date(`2000-01-01T${slot.end_time}`);
          const checkTime = new Date(`2000-01-01T${timeString}`);
          return checkTime >= startTime && checkTime <= endTime;
        }
        return false;
      });
    }

    if (schedule.type === 'on_call') {
      if (!rawTime) return false;
      const [requestedStart, requestedEnd] = rawTime.split('-').map((part) => part?.trim());

      if (!requestedEnd) {
        const startTime = new Date(`2000-01-01T${schedule.on_call_start_time}`);
        const endTime = new Date(`2000-01-01T${schedule.on_call_end_time}`);
        startTime.setMinutes(startTime.getMinutes() + 1);
        endTime.setMinutes(endTime.getMinutes() - 1);
        const checkTime = new Date(`2000-01-01T${requestedStart || rawTime}`);
        return checkTime >= startTime && checkTime <= endTime;
      }

      return (
        schedule.on_call_start_time.substring(0, 5) === requestedStart &&
        schedule.on_call_end_time.substring(0, 5) === requestedEnd
      );
    }

    return false;
  });

  return match || null;
};

export const getStartWaypoint = (waypoints = []) => {
  if (!Array.isArray(waypoints) || !waypoints.length) {
    return null;
  }

  return waypoints.reduce((earliest, waypoint) => {
    if (!earliest) return waypoint;
    return waypoint.sequence_number < earliest.sequence_number ? waypoint : earliest;
  }, waypoints[0]) ?? null;
};

export const getStartLocationId = (waypoints = []) => {
  return getStartWaypoint(waypoints)?.id ?? null;
};

/**
 * Check if the pickup stop is the start location of the route.
 * @param {Array} waypoints - Route waypoints with id and sequence_number
 * @param {number|string} pickupStopId - The pickup waypoint ID
 * @returns {{ isPickupStart: boolean, startName: string }}
 */
export const getPickupStartInfo = (waypoints, pickupStopId) => {
  const startWp = getStartWaypoint(waypoints);
  if (!startWp || !pickupStopId) {
    return { isPickupStart: true, startName: '' };
  }
  return {
    isPickupStart: Number(startWp.id) === Number(pickupStopId),
    startName: startWp.name || ''
  };
};

const resolveDateString = (selectedDate) => {
  if (!selectedDate) {
    return null;
  }

  if (typeof selectedDate === 'string') {
    return selectedDate;
  }

  if (selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())) {
    return formatDateISO(selectedDate);
  }

  return null;
};

export const filterSlotsByAdvanceBooking = ({
  slots = [],
  selectedDate = null,
  minimumAdvanceHours = 0,
  bookingScope = 'all_stops',
  pickupStopId = null,
  waypoints = [],
  entityTimezone = 'UTC',
  bypassAdvanceBookingRestriction = false // Admin bypass flag
}) => {
  if (!slots.length) {
    return [];
  }

  const dateString = resolveDateString(selectedDate);
  if (!dateString) {
    return slots;
  }

  const tz = entityTimezone || 'UTC';
  const now = moment.tz(tz);
  const startLocationId = getStartLocationId(waypoints);
  const isPickupAtStartLocation = pickupStopId &&
    startLocationId &&
    Number(pickupStopId) === Number(startLocationId);

  // Admin bypass: Skip advance booking time restriction, but still check for past slots
  const shouldApplyScope = !bypassAdvanceBookingRestriction && minimumAdvanceHours > 0 && (
    bookingScope === 'all_stops' ||
    (bookingScope === 'start_only' && isPickupAtStartLocation)
  );

  return slots.map((slot) => {
    const slotTime = slot.type === 'fixed' ? slot.time : slot.startTime;
    if (!slotTime) {
      return slot;
    }

    const slotDateTime = moment.tz(
      `${dateString} ${slotTime}`,
      'YYYY-MM-DD HH:mm',
      tz
    );
    const hoursUntilDeparture = slotDateTime.diff(now, 'hours', true);

    // Calculate if slot time has passed (for display purposes)
    // Note: We do NOT disable past slots here - the backend pickupStopPassed check
    // determines if booking is allowed based on actual shuttle position.
    // This allows "book after departure if pickup not passed" flow to work.
    let isPastDepartureTime;
    if (slot.type === 'on_call' && slot.endTime) {
      const slotEndDateTime = moment.tz(
        `${dateString} ${slot.endTime}`,
        'YYYY-MM-DD HH:mm',
        tz
      );
      isPastDepartureTime = slotEndDateTime.isSameOrBefore(now);
    } else {
      isPastDepartureTime = slotDateTime.isSameOrBefore(now);
    }

    // For advance booking check:
    // - Fixed slots: check against the departure time
    // - On-call slots: check against the END of the window, since users can book
    //   any time within the window. Only disable if no bookable time remains.
    let violatesAdvance = false;
    if (shouldApplyScope) {
      if (slot.type === 'on_call' && slot.endTime) {
        const slotEndDateTime = moment.tz(
          `${dateString} ${slot.endTime}`,
          'YYYY-MM-DD HH:mm',
          tz
        );
        const hoursUntilWindowEnd = slotEndDateTime.diff(now, 'hours', true);
        violatesAdvance = hoursUntilWindowEnd <= minimumAdvanceHours;
      } else {
        violatesAdvance = hoursUntilDeparture <= minimumAdvanceHours;
      }
    }

    let disabledReason = slot.disabledReason || null;
    if (violatesAdvance) {
      disabledReason = 'tooSoon';
    }
    // Note: isPastDepartureTime is passed through but NOT used to disable the slot.
    // The enhanceSlotsWithCapacity function will check pickupStopPassed from backend
    // to determine if the slot should be disabled for past departures.

    return {
      ...slot,
      slotDate: dateString,
      disabled: Boolean(slot.disabled || violatesAdvance),
      disabledReason,
      hoursUntilDeparture,
      isPastDepartureTime // Pass through for capacity enhancement to use
    };
  });
};

const parseTimeToMinutes = (time) => {
  if (!time || typeof time !== 'string') return null;
  const [hours, minutes] = time.split(':').map((value) => parseInt(value, 10));
  if ([hours, minutes].some(Number.isNaN)) return null;
  return hours * 60 + minutes;
};

const hasAvailableOnCallTime = ({ slot, windowTrips, entityTimezone }) => {
  if (!slot?.slotDate || !slot?.startTime || !slot?.endTime) {
    return true;
  }

  const minGap = Math.max(parseInt(slot.minGap, 10) || 0, 0);
  if (minGap <= 0) {
    return true;
  }

  const tz = entityTimezone || 'UTC';
  const windowStart = moment.tz(`${slot.slotDate} ${slot.startTime}`, 'YYYY-MM-DD HH:mm', tz);
  const windowEnd = moment.tz(`${slot.slotDate} ${slot.endTime}`, 'YYYY-MM-DD HH:mm', tz);
  if (!windowStart.isValid() || !windowEnd.isValid()) {
    return true;
  }

  const now = moment.tz(tz);
  const effectiveStart = moment.max(windowStart, now);
  if (effectiveStart.isAfter(windowEnd)) {
    return false;
  }

  const windowStartMin = parseTimeToMinutes(windowStart.format('HH:mm'));
  const windowEndMin = parseTimeToMinutes(windowEnd.format('HH:mm'));
  const effectiveStartMin = parseTimeToMinutes(effectiveStart.format('HH:mm'));
  if (windowStartMin == null || windowEndMin == null || effectiveStartMin == null) {
    return true;
  }

  if (!windowTrips?.length) {
    return effectiveStartMin <= windowEndMin;
  }

  const blockedIntervals = windowTrips
    .map((entry) => parseTimeToMinutes(entry.time))
    .filter((timeMin) => timeMin != null)
    .map((timeMin) => {
      const start = Math.max(windowStartMin, timeMin - minGap + 1);
      const end = Math.min(windowEndMin, timeMin + minGap - 1);
      return start <= end ? { start, end } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start);

  if (!blockedIntervals.length) {
    return effectiveStartMin <= windowEndMin;
  }

  let cursor = Math.max(effectiveStartMin, windowStartMin);
  for (const interval of blockedIntervals) {
    if (cursor < interval.start) {
      return true;
    }
    if (cursor <= interval.end) {
      cursor = interval.end + 1;
      if (cursor > windowEndMin) {
        return false;
      }
    }
  }

  return cursor <= windowEndMin;
};

const getEntryAvailableSeats = (entry) => Number(
  entry?.availableOnSegment ??
  entry?.available_on_segment ??
  entry?.availableAtPickup ??
  entry?.available_at_pickup ??
  entry?.remainingSeats ??
  entry?.remaining_seats ??
  0
);

const getEntryAvailableLuggage = (entry) => Number(
  entry?.availableLuggageOnSegment ??
  entry?.available_luggage_on_segment ??
  entry?.availableLuggageAtPickup ??
  entry?.available_luggage_at_pickup ??
  entry?.remainingLuggage ??
  entry?.remaining_luggage ??
  0
);

const getWindowTripsForSlot = (slot, capacityData = []) => {
  if (slot?.type !== 'on_call' || !slot?.startTime || !slot?.endTime) {
    return [];
  }

  return capacityData.filter((entry) => {
    if (!entry?.time) return false;
    return entry.time >= slot.startTime && entry.time <= slot.endTime;
  });
};

const getWindowFailureReason = (windowTrips = []) => {
  const reasons = windowTrips
    .map((entry) => entry?.accessibleVehicleFailureReason)
    .filter(Boolean);

  if (!reasons.length) {
    return null;
  }

  if (reasons.includes('no_vehicle_with_accessible_capacity')) {
    return 'no_vehicle_with_accessible_capacity';
  }

  if (reasons.includes('no_vehicle_with_luggage_capacity')) {
    return 'no_vehicle_with_luggage_capacity';
  }

  return reasons[0];
};

const getBestWindowCapacityPreview = (windowTrips = []) => windowTrips.reduce((best, current) => {
  if (!best) {
    return current;
  }

  const currentSeats = getEntryAvailableSeats(current);
  const bestSeats = getEntryAvailableSeats(best);
  if (currentSeats !== bestSeats) {
    return currentSeats > bestSeats ? current : best;
  }

  const currentLuggage = getEntryAvailableLuggage(current);
  const bestLuggage = getEntryAvailableLuggage(best);
  return currentLuggage > bestLuggage ? current : best;
}, null);

export const enhanceSlotsWithCapacity = ({
  slots = [],
  capacityData = [],
  ridersRequested = 1,
  luggageRequested = 0,
  requestAccessibleVehicle = false,
  entityTimezone = 'UTC'
}) => {
  if (!slots.length) {
    return [];
  }

  const normalizedRiders = Math.max(parseInt(ridersRequested, 10) || 1, 1);
  const normalizedLuggage = Math.max(parseInt(luggageRequested, 10) || 0, 0);
  const eligibilityUnavailable = (entry) => {
    const state = entry?.bookingStopEligibility?.state;
    return state != null && state !== 'available';
  };
  const eligibilityDisabledReason = (entry) => {
    const eligibility = entry?.bookingStopEligibility;
    if (!eligibility || eligibility.state === 'available') return null;
    const endpoints = eligibility.endpoints || [];
    if (endpoints.includes('pickup') && endpoints.includes('dropoff')) {
      return 'bookingStopsUnavailable';
    }
    if (endpoints.includes('pickup')) return 'bookingPickupUnavailable';
    if (endpoints.includes('dropoff')) return 'bookingDropoffUnavailable';
    return 'bookingStopEligibilityUnresolved';
  };

  return slots.map((slot) => {
    // For fixed slots, match by exact time
    // For on-call slots, match trips whose departure time falls within the window
    let capacityInfo;
    const windowTrips = getWindowTripsForSlot(slot, capacityData);
    if (slot.type === 'fixed') {
      capacityInfo = capacityData.find((entry) => (
        entry.time === slot.time
        && (
          slot.scheduleId == null
          || (
            (entry.scheduleId ?? entry.occurrenceScheduleId) != null
            && Number(entry.scheduleId ?? entry.occurrenceScheduleId)
              === Number(slot.scheduleId)
          )
        )
      ));
    } else if (slot.type === 'on_call' && slot.startTime && slot.endTime) {
      // For on-call windows, be optimistic: only block if ALL trips have passed
      // Use the BEST available capacity since user can pick any time in the window
      if (windowTrips.length > 0) {
        // Check if at least one trip is still available (not passed)
        const availableTrips = windowTrips.filter(trip =>
          !(trip.pickupStopPassed || trip.pickup_stop_passed) &&
          !eligibilityUnavailable(trip) &&
          (!requestAccessibleVehicle || trip.accessibleVehicleAvailable !== false)
        );

        if (availableTrips.length > 0) {
          // Use the best capacity from available trips
          capacityInfo = availableTrips.reduce((best, current) => {
            if (!best) return current;
            const currentSeats = current.availableOnSegment ?? current.available_on_segment ??
              current.availableAtPickup ?? current.available_at_pickup ??
              current.remainingSeats ?? current.remaining_seats ?? 0;
            const bestSeats = best.availableOnSegment ?? best.available_on_segment ??
              best.availableAtPickup ?? best.available_at_pickup ??
              best.remainingSeats ?? best.remaining_seats ?? 0;
            return currentSeats > bestSeats ? current : best;
          }, null);
          // Override pickupStopPassed to false since at least one trip is available
          if (capacityInfo) {
            capacityInfo = { ...capacityInfo, pickupStopPassed: false, pickup_stop_passed: false };
          }
        }
      }
    } else {
      capacityInfo = capacityData.find((entry) => entry.time === slot.startTime);
    }

    const accessibleFailureReason =
      capacityInfo?.accessibleVehicleFailureReason ??
      getWindowFailureReason(windowTrips);
    const hasSpecificAccessibleCapacityReason =
      accessibleFailureReason === 'no_vehicle_with_accessible_capacity' ||
      accessibleFailureReason === 'no_vehicle_with_luggage_capacity';

    if (!capacityInfo && requestAccessibleVehicle && hasSpecificAccessibleCapacityReason) {
      capacityInfo = getBestWindowCapacityPreview(windowTrips);
    }

    const noAvailableGap = slot.type === 'on_call' &&
      !hasAvailableOnCallTime({
        slot,
        windowTrips,
        entityTimezone
      });
    const onCallOperationalState = slot.type === 'on_call'
      ? capacityData.find((entry) => (
        entry.type === 'on_call'
        && entry.startTime === slot.startTime
        && entry.endTime === slot.endTime
      ))
      : null;
    const operationallyUnavailable = slot.type === 'on_call'
      ? onCallOperationalState?.operationallyAvailable === false
      : capacityInfo?.operationallyAvailable === false;
    const assignmentUnavailable = slot.type === 'on_call'
      ? onCallOperationalState?.assignmentAvailable === false
      : capacityInfo?.assignmentAvailable === false;
    const operationalBlockedIntervals = slot.type === 'on_call'
      ? onCallOperationalState?.operationalBlockedIntervals
      : undefined;

    const noAccessibleVehicle = Boolean(
      requestAccessibleVehicle && (
        accessibleFailureReason !== 'no_vehicle_with_accessible_capacity' &&
        accessibleFailureReason !== 'no_vehicle_with_luggage_capacity' &&
        ((slot.type === 'fixed' && capacityInfo && capacityInfo.accessibleVehicleAvailable === false) ||
        (slot.type === 'on_call' && (() => {
          if (!windowTrips.length) {
            return false;
          }

          return !windowTrips.some((entry) =>
            !(entry.pickupStopPassed || entry.pickup_stop_passed) &&
            !eligibilityUnavailable(entry) &&
            entry.accessibleVehicleAvailable !== false
          );
        })()))
      )
    );

    if (!capacityInfo) {
      // No capacity data means no active trip for this slot.
      // If departure time has passed and no trip exists, disable the slot.
      const isPast = slot.isPastDepartureTime === true;
      const baseSlot = {
        ...slot,
        remainingSeats: slot.remainingSeats ?? null,
        totalCapacity: slot.totalCapacity ?? null,
        potentialWithReorder: false,
        pickupStopPassed: false,
        accessibleVehicleConfigured: capacityInfo?.accessibleVehicleConfigured ?? null,
        accessibleVehicleOfflineMessage: capacityInfo?.accessibleVehicleOfflineMessage ?? null,
        accessibleVehicleFailureReason: accessibleFailureReason,
        operationalBlockedIntervals,
        // Disable if past departure and no active trip (no way to check passage)
        disabled: Boolean(
          slot.disabled || isPast || operationallyUnavailable || assignmentUnavailable
        ),
        disabledReason: operationallyUnavailable
          ? 'operationalUnavailability'
          : (assignmentUnavailable
            ? 'noVehicleAvailable'
            : (isPast ? 'inPast' : slot.disabledReason)),
        hasVehicle: false,
      };
      if (noAccessibleVehicle) {
        return {
          ...baseSlot,
          disabled: true,
          disabledReason: baseSlot.disabled ? baseSlot.disabledReason : 'noAccessibleVehicle'
        };
      }
      if (noAvailableGap) {
        return {
          ...baseSlot,
          disabled: true,
          disabledReason: baseSlot.disabled ? baseSlot.disabledReason : 'noAvailableGap'
        };
      }
      return baseSlot;
    }

    // Check if pickup stop has been physically passed (for active trips)
    const pickupStopPassed = Boolean(
      capacityInfo.pickupStopPassed ??
      capacityInfo.pickup_stop_passed ??
      false
    );
    const stopEligibilityReason = eligibilityDisabledReason(capacityInfo);
    const hasLiveTrip = Boolean(
      capacityInfo.tripId ??
      capacityInfo.trip_id ??
      false
    );
    const isPastWithoutLiveTrip = slot.isPastDepartureTime === true && !hasLiveTrip;

    // Position-aware capacity uses segment availability when provided, otherwise pickup or legacy totals
    const remainingSeats = Number(
      capacityInfo.availableOnSegment ??
      capacityInfo.available_on_segment ??
      capacityInfo.availableAtPickup ??
      capacityInfo.available_at_pickup ??
      capacityInfo.remainingSeats ??
      capacityInfo.remaining_seats ??
      0
    );
    const totalCapacity = Number(capacityInfo.vehicleCapacity ?? capacityInfo.vehicle_capacity ?? 0);
    const hasVehicle = Boolean(
      capacityInfo.hasVehicle
      ?? capacityInfo.has_vehicle
      ?? totalCapacity > 0
    );

    // Luggage capacity (position-aware)
    const remainingLuggage = capacityInfo.availableLuggageOnSegment ??
      capacityInfo.available_luggage_on_segment ??
      capacityInfo.availableLuggageAtPickup ??
      capacityInfo.available_luggage_at_pickup ?? null;
    const totalLuggageCapacity = Number(
      capacityInfo.vehicleLuggageCapacity ?? capacityInfo.vehicle_luggage_capacity ?? 0
    );

    // Check if route reordering could potentially free up capacity
    const potentialWithReorder = Boolean(capacityInfo.potentialWithReorder ?? capacityInfo.potential_with_reorder ?? false);
    // A positive assigned or candidate-preview total is capacity authority.
    const hasCapacityAuthority = totalCapacity > 0;
    const notEnoughSeats = hasCapacityAuthority && remainingSeats < normalizedRiders;
    const hasLuggageCapacity = totalLuggageCapacity > 0;
    const notEnoughLuggage = hasLuggageCapacity && remainingLuggage !== null && normalizedLuggage > remainingLuggage;

    // Determine disabled state and reason
    // Priority: pickupStopPassed > capacity issues
    // If slot is full but has potential with reorder, show special indicator instead of disabling
    const shouldDisable = isPastWithoutLiveTrip ||
      operationallyUnavailable ||
      assignmentUnavailable ||
      Boolean(stopEligibilityReason) ||
      pickupStopPassed ||
      noAccessibleVehicle ||
      ((notEnoughSeats || notEnoughLuggage) && !potentialWithReorder);
    let reason = slot.disabledReason;
    if (operationallyUnavailable) {
      reason = 'operationalUnavailability';
    } else if (assignmentUnavailable) {
      reason = 'noVehicleAvailable';
    } else if (stopEligibilityReason) {
      reason = stopEligibilityReason;
    } else if (isPastWithoutLiveTrip) {
      reason = 'inPast';
    } else if (pickupStopPassed) {
      reason = 'pickupStopPassed';
    } else if (noAccessibleVehicle) {
      reason = 'noAccessibleVehicle';
    } else if (notEnoughSeats || notEnoughLuggage) {
      if (potentialWithReorder) {
        reason = 'potentialWithReorder';
      } else if (notEnoughSeats && notEnoughLuggage) {
        reason = 'insufficientBoth';
      } else if (notEnoughSeats) {
        reason = requestAccessibleVehicle && accessibleFailureReason === 'no_vehicle_with_accessible_capacity'
          ? 'insufficientAccessibleCapacity'
          : remainingSeats <= 0
            ? 'fullyBooked'
            : 'insufficientCapacity';
      } else {
        reason = 'insufficientLuggage';
      }
    }

    const baseSlot = {
      ...slot,
      disabled: Boolean(slot.disabled || shouldDisable),
      disabledReason: slot.disabled ? slot.disabledReason : reason,
      remainingSeats,
      totalCapacity,
      hasVehicle,
      remainingLuggage,
      totalLuggageCapacity,
      potentialWithReorder,
      pickupStopPassed,
      accessibleVehicleConfigured: capacityInfo.accessibleVehicleConfigured ?? null,
      accessibleVehicleOfflineMessage: capacityInfo.accessibleVehicleOfflineMessage ?? null,
      accessibleVehicleFailureReason: accessibleFailureReason,
      tripId: capacityInfo.tripId ?? capacityInfo.trip_id ?? slot.tripId ?? null,
      // A fixed slot represents the exact trip matched above. An on-call
      // window can contain several possible times, so it must wait until the
      // rider chooses a concrete trip before carrying trip-specific Delay.
      operationalDelay: slot.type === 'fixed'
        ? (capacityInfo.operationalDelay ?? null)
        : undefined,
      operationalBlockedIntervals
    };
    if (noAvailableGap) {
      return {
        ...baseSlot,
        disabled: true,
        disabledReason: baseSlot.disabled ? baseSlot.disabledReason : 'noAvailableGap'
      };
    }
    return baseSlot;
  });
};
